import { useState, useEffect, useRef, useMemo } from "react"; 
import { 
  TextField, 
  Autocomplete, 
  Box, Divider, Typography, IconButton,
  Snackbar, Alert, CircularProgress, Grid, Paper
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/en-gb';
import EditPage from "./EditPage";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import PetsIcon from '@mui/icons-material/Pets';
import { uploadFiles, uploadSingleFile, markFileAsDeleted, prepareFilesForBackend, getProgressMessage } from '../../utils/fileUploadHelper';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import InlinePhotoCapture from "../../components/InlinePhotoCapture";
import { 
  fetchVetStudies, 
  validateNonEmpty,
  handleError,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateTimeFormatForLocale,
  resolveHour12Preference,
} from "../../utils/lib";
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../providers/ConfigProvider';

const EditStudy = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { updateTitle, toggleFab } = useAppBar();
  const [ isLoading, setIsLoading ] = useState(false);
  const { t } = useTranslation();
  const { config, isLoadingConfig } = useConfig();

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const useHour12 = useMemo(
    () => resolveHour12Preference(config, dateFormattingLocale),
    [config, dateFormattingLocale]
  );
  const dateTimePickerFormat = useMemo(
    () => getDayjsDateTimeFormatForLocale(dateFormattingLocale, useHour12),
    [dateFormattingLocale, useHour12]
  );
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );

  const { updateStudy, pet } = location.state || {};

  const [study, setStudy] = useState(() => {
    if (updateStudy) {
      let dateValue = updateStudy.date;
      // Always reconstruct as Dayjs, even if it looks like a Dayjs object
      dateValue = dayjs(
        typeof dateValue === 'object' && dateValue !== null && dateValue.$d
          ? dateValue.$d
          : dateValue
      );
      return {
        ...updateStudy,
        date: dateValue,
        pet: pet
      };
    }
    return {
      date: dayjs(),
      product: null,
      notes: "",
      pet: pet,
    };
  });

  const [errors, setErrors] = useState({});
  const [refs, setRefs] = useState({
    product: useRef(null),
  });
 
  const [products, setProducts] = useState([]);
 
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ open: false, text: '', severity: 'info' });
  const fileInputRef = useRef(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showInlineCamera, setShowInlineCamera] = useState(false);
  const { setNavigation, from, state: navState, resetNavigation } = useNavigationHelperStore();

  useEffect(() => {
    setIsLoading(true);

    updateTitle(
      (pet?.thumb_url) || study?.pet?.thumb_url || '',
      (pet?.name) || study?.pet?.name || '',
      study.id ? t('edit.editStudy') : t('edit.newStudy'),
      false,
      PetsIcon
    );

    if (study.resources) {
      setAttachedFiles(study.resources);
    }

    toggleFab(false);
    setIsLoading(false);

    const fetchData = async () => {
      const vetStudies = await fetchVetStudies();
      setProducts(vetStudies);

      setIsLoading(false);

      if (refs.product.current) {
        refs.product.current.focus();
      } 
    };
    fetchData();
  }, []);

  // Restaurar borrador si volvemos desde la cámara
  useEffect(() => {
    if (from === '/main/edit-study' && navState) {
      if (navState.study) {
        setStudy((prev) => ({
          ...prev,
          ...navState.study,
          date: dayjs(navState.study.date)
        }));
      }
      if (navState.attachedFiles) {
        setAttachedFiles(navState.attachedFiles);
      }
    }
  }, [from, navState]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setStudy({ ...study, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !validateNonEmpty("product", study.product?.name, setErrors, refs.product)
    )
      return;

      save();
  };

  const save = async () => {
    setIsLoading(true);

    try {
      let response;
      const studyData = {
        ...study,
        date: dayjs(study.date).format('YYYY-MM-DD HH:mm:ss'),
        resources: prepareFilesForBackend(attachedFiles),
        values: []
      };

      //console.log(studyData);

      if (studyData.id) {
        response = await axios.put(
          `${import.meta.env.VITE_API_URL}pets_studies?id=${studyData.id}`,
          studyData,
          { withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${import.meta.env.VITE_API_URL}pets_studies`,
        studyData,
        { withCredentials: true }
      );
      }

      //navigate(-1);
      navigate('/main/view-pet', { state: { id: study?.pet?.id, updateLastView: false, ...navState?.state } }, { replace: true });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
      resetNavigation();
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setUploading(true);
    setUploadMessage({
      open: true,
      text: t('common.uploadingFiles'),
      severity: 'info'
    });

    try {
      const result = await uploadFiles({
        files,
        tableName: 'pets_studies',
        type: 'PRIVATE',
        onProgress: (progress, file, index, total) => {
          setUploadMessage({
            open: true,
            text: getProgressMessage(index, total, progress),
            severity: 'info'
          });
        },
        onFileComplete: (uploadedFile) => {
          setAttachedFiles(prev => [...prev, uploadedFile]);
        },
        onError: (error, file) => {
          setUploadMessage({
            open: true,
            text: error,
            severity: 'error'
          });
        }
      });

      if (result.success) {
        setUploadMessage({
          open: true,
          text: t('common.filesUploadedSuccess'),
          severity: 'success'
        });
      } else if (result.successfulUploads > 0) {
        setUploadMessage({
          open: true,
          text: t('common.partialUploadSuccess', { success: result.successfulUploads, total: result.totalFiles }),
          severity: 'warning'
        });
      }
    } catch (error) {
      setUploadMessage({
        open: true,
        text: t('common.uploadError'),
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (index) => {
    const fileToDelete = attachedFiles[index];
    const updatedFiles = markFileAsDeleted(attachedFiles, fileToDelete.unique_id);
    setAttachedFiles(updatedFiles);
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleCameraClick = () => {
    setShowInlineCamera(true);
  };

  const handleInlinePhotoConfirm = async (file, previewUrl) => {
    setUploading(true);
    setUploadMessage({ open: true, text: t('common.uploadingPhoto'), severity: 'info' });
    
    try {
      const uploaded = await uploadSingleFile({
        file: file,
        tableName: 'pets_studies',
        type: 'PRIVATE',
        onProgress: (progress) => {
          setUploadMessage({ open: true, text: getProgressMessage(0, 1, progress), severity: 'info' });
        }
      });
      setAttachedFiles((prev) => [...prev, uploaded]);
      setUploadMessage({ open: true, text: t('common.photoUploadedSuccess'), severity: 'success' });
    } catch (error) {
      setUploadMessage({ open: true, text: error.message || t('common.photoUploadError'), severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <EditPage 
      onSubmit={handleSubmit}
      isLoading={isLoading || isLoadingConfig}
    >
      <Box sx={{ width: "100%", marginTop: 1 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <DateTimePicker
            label={t('common.dateAndTime')}
            value={study.date}
            onChange={(newValue) => setStudy({ ...study, date: newValue })}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                margin="normal"
                name="date"
                error={!!errors.date}
                helperText={errors.date}
              />
            )}
            slotProps={{ textField: { fullWidth: true } }}
            format={dateTimePickerFormat}
            ampm={useHour12}
          />
        </LocalizationProvider>
      </Box>

      <Autocomplete
        fullWidth
        options={products}
        getOptionLabel={(option) => (option ? option.name : "")}
        value={study.product}
        onChange={(e, newValue) => {
          setStudy({ ...study, product: newValue });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('common.study')}
            name="product"
            margin="normal"
            inputRef={refs.product}
            error={!!errors.product}
            helperText={errors.product}
          />
        )}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.notes')}
        name="notes"
        value={study.notes}
        onChange={handleChange}
      />

      <Divider sx={{ my: 2 }} />

      <InlinePhotoCapture 
        open={showInlineCamera}
        onPhotoConfirm={handleInlinePhotoConfirm}
        onClose={() => setShowInlineCamera(false)}
      />

      <Box display="flex" gap={4} justifyContent="center">
        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: 'none' }}
          />
          <IconButton onClick={handleUploadClick} disabled={uploading}>
            <AttachFileIcon />
          </IconButton>
          <Typography variant="caption">{t('common.file')}</Typography>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
          <IconButton onClick={handleCameraClick} disabled={uploading}>
            <PhotoCameraIcon />
          </IconButton>
          <Typography variant="caption">{t('common.photo')}</Typography>
        </Box>
      </Box>

      {attachedFiles.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {attachedFiles.filter(file => file.deleted !== 1).map((file, filteredIndex) => {
              // Encontrar el índice original del archivo en el array completo
              const originalIndex = attachedFiles.findIndex(f => f.unique_id === file.unique_id);
              
              return (
                <Grid item xs={4} key={file.unique_id}>
                <Paper 
                  sx={{ 
                    position: 'relative',
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                  {file.type.startsWith('image/') ? (
                    <img 
                      src={file.url} 
                      alt={file.name}
                      style={{ 
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <AttachFileIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  )}
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)'
                    }}
                    onClick={() => handleDeleteFile(originalIndex)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    textAlign: 'center',
                    mt: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {file.name}
                </Typography>
              </Grid>
              );
            })}
          </Grid>
        </>
      )}

      <Snackbar
        open={uploadMessage.open} 
        autoHideDuration={6000}
        onClose={() => setUploadMessage(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setUploadMessage(prev => ({ ...prev, open: false }))} 
          severity={uploadMessage.severity}
          sx={{ width: '100%' }}
        >
          {uploadMessage.text}
        </Alert>
      </Snackbar>

    </EditPage>
  );
};

export default EditStudy;